// src/components/FinanceScreen.jsx
// Central financeira unificada do PsyAssist.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileHeart,
  Filter,
  Landmark,
  LoaderCircle,
  Menu,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingDown,
  TrendingUp,
  WalletCards,
  X,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

import "../sass/pages/finance.scss";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  RECURSOS: "recursos",
};

const PERIOD_OPTIONS = [
  { value: "mes", label: "Este mês" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "90d", label: "Últimos 90 dias" },
  { value: "ano", label: "Este ano" },
  { value: "all", label: "Todo o período" },
];

const SORT_OPTIONS = [
  { value: "date-desc", label: "Mais recentes" },
  { value: "date-asc", label: "Mais antigas" },
  { value: "value-desc", label: "Maior valor" },
  { value: "value-asc", label: "Menor valor" },
];

const MOVEMENT_TYPES = [
  { value: "todos", label: "Todas" },
  { value: "receita", label: "Receitas" },
  { value: "despesa", label: "Despesas" },
];

function readAuth(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function saveAuthToken(token) {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function moneyBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function percentBR(value) {
  return `${safeNumber(value).toFixed(1).replace(".", ",")}%`;
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);
  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatTimeBR(value) {
  const date = parseDate(value);
  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatPeriodLabel(period = {}) {
  if (period?.label) return period.label;

  if (period?.dataInicio && period?.dataFim) {
    return `${formatDateBR(period.dataInicio)} até ${formatDateBR(period.dataFim)}`;
  }

  return "Período atual";
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function canUsePremiumFinanceAI() {
  const resources = parseJsonStorage(readAuth(AUTH_KEYS.RECURSOS), {});

  if (resources?.ia === true || resources?.insightsIa === true) {
    return true;
  }

  const plan = normalizeText(readAuth(AUTH_KEYS.PLAN));
  const status = normalizeText(
    readAuth(AUTH_KEYS.STATUS_ASSINATURA) || "active"
  );

  const activeRaw = readAuth(AUTH_KEYS.ASSINATURA_ATIVA);
  const active =
    activeRaw === "" ||
    String(activeRaw).trim().toLowerCase() !== "false";

  return (
    plan.includes("premium") &&
    ["active", "trialing", "past_due"].includes(status) &&
    active
  );
}

function getHealthInfo(summary = {}) {
  const revenue = safeNumber(summary.receita);
  const expense = safeNumber(summary.despesa);
  const balance = safeNumber(summary.saldo, revenue - expense);
  const risk = safeNumber(summary.riscoFinanceiro);

  if (revenue <= 0 && expense <= 0) {
    return {
      tone: "neutral",
      label: "Sem movimento",
      title: "Comece pelos lançamentos",
      description:
        "Ainda não há movimentações suficientes no período selecionado.",
    };
  }

  if (balance < 0) {
    return {
      tone: "bad",
      label: "Atenção",
      title: "Saídas acima das entradas",
      description:
        "O período está com saldo negativo. Revise custos e próximos compromissos.",
    };
  }

  if (risk >= 70) {
    return {
      tone: "bad",
      label: "Risco alto",
      title: "Despesas pressionando a receita",
      description:
        "Uma parcela elevada das entradas está comprometida por despesas.",
    };
  }

  if (risk >= 45) {
    return {
      tone: "warn",
      label: "Acompanhar",
      title: "Financeiro em zona de atenção",
      description:
        "O saldo está positivo, mas vale acompanhar custos recorrentes.",
    };
  }

  return {
    tone: "good",
    label: "Saudável",
    title: "Financeiro equilibrado",
    description:
      "Entradas e saídas estão em uma relação confortável no período.",
  };
}

function FinanceSkeleton() {
  return (
    <div className="finc-skeleton" aria-hidden="true">
      <div className="finc-skeleton__hero" />

      <div className="finc-skeleton__metrics">
        <span />
        <span />
        <span />
        <span />
      </div>

      <div className="finc-skeleton__panel" />

      <div className="finc-skeleton__rows">
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  open,
  movement,
  loading,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  const isExpense = movement?.tipo === "despesa";

  return (
    <div
      className="finc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="finc-delete-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section className="finc-modal">
        <div className="finc-modal__icon">
          <Trash2 size={22} aria-hidden="true" />
        </div>

        <div className="finc-modal__content">
          <span>Exclusão definitiva</span>

          <h2 id="finc-delete-title">
            Excluir {isExpense ? "despesa" : "receita"}?
          </h2>

          <p>
            Você está prestes a excluir{" "}
            <strong>
              {movement?.descricao ||
                movement?.categoria ||
                "esta movimentação"}
            </strong>
            , no valor de{" "}
            <strong>{moneyBR(movement?.valor)}</strong>.
          </p>

          <small>
            {isExpense
              ? "A despesa usa a mesma exclusão atual e mantém a regra já existente para as parcelas vinculadas."
              : "Essa ação não poderá ser desfeita."}
          </small>
        </div>

        <div className="finc-modal__actions">
          <button
            type="button"
            className="finc-btn finc-btn--ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="finc-btn finc-btn--danger"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoaderCircle size={16} className="finc-spin" />
                Excluindo...
              </>
            ) : (
              <>
                <Trash2 size={16} aria-hidden="true" />
                Excluir
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

function SummaryMetric({
  icon: Icon,
  eyebrow,
  value,
  detail,
  tone,
}) {
  return (
    <article className={`finc-metric finc-metric--${tone}`}>
      <div className="finc-metric__icon">
        <Icon size={20} aria-hidden="true" />
      </div>

      <div className="finc-metric__content">
        <span>{eyebrow}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function EvolutionChart({ rows = [] }) {
  const normalized = (Array.isArray(rows) ? rows : []).map((item) => ({
    ...item,
    receita: Math.max(0, safeNumber(item?.receita)),
    despesa: Math.max(0, safeNumber(item?.despesa)),
    saldo: safeNumber(item?.saldo),
  }));

  const max = Math.max(
    1,
    ...normalized.flatMap((item) => [item.receita, item.despesa])
  );

  if (!normalized.length) {
    return (
      <div className="finc-chart-empty">
        <BarChart3 size={24} />
        <strong>Ainda não há histórico suficiente</strong>
        <span>
          Os próximos períodos serão exibidos conforme os lançamentos forem
          registrados.
        </span>
      </div>
    );
  }

  return (
    <div className="finc-chart">
      <div className="finc-chart__legend">
        <span>
          <i className="is-revenue" />
          Receitas
        </span>

        <span>
          <i className="is-expense" />
          Despesas
        </span>
      </div>

      <div className="finc-chart__plot">
        {normalized.map((item) => {
          const revenueHeight = Math.max(3, (item.receita / max) * 100);
          const expenseHeight = Math.max(3, (item.despesa / max) * 100);

          return (
            <div
              className="finc-chart__month"
              key={item.mes || item.label}
            >
              <div className="finc-chart__bars">
                <span
                  className="finc-chart__bar finc-chart__bar--revenue"
                  style={{ height: `${revenueHeight}%` }}
                  title={`Receitas: ${moneyBR(item.receita)}`}
                />

                <span
                  className="finc-chart__bar finc-chart__bar--expense"
                  style={{ height: `${expenseHeight}%` }}
                  title={`Despesas: ${moneyBR(item.despesa)}`}
                />
              </div>

              <strong>{item.label || item.mes}</strong>

              <small className={item.saldo < 0 ? "is-negative" : ""}>
                {moneyBR(item.saldo)}
              </small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryDistribution({ rows = [] }) {
  const items = Array.isArray(rows) ? rows.slice(0, 5) : [];

  const max = Math.max(
    1,
    ...items.map((item) => safeNumber(item?.valor))
  );

  if (!items.length) {
    return (
      <div className="finc-compact-empty">
        Nenhuma despesa categorizada no período.
      </div>
    );
  }

  return (
    <div className="finc-category-list">
      {items.map((item) => {
        const value = safeNumber(item?.valor);

        return (
          <article key={item?.categoria || item?.label}>
            <div>
              <strong>
                {item?.categoria || item?.label || "Outros"}
              </strong>

              <span>{moneyBR(value)}</span>
            </div>

            <div className="finc-category-list__bar">
              <span
                style={{
                  width: `${Math.max(4, (value / max) * 100)}%`,
                }}
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function FinanceScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [period, setPeriod] = useState(
    searchParams.get("period") || "mes"
  );

  const [type, setType] = useState(
    searchParams.get("type") || "todos"
  );

  const [sort, setSort] = useState(
    searchParams.get("sort") || "date-desc"
  );

  const [page, setPage] = useState(
    Math.max(1, Number(searchParams.get("page")) || 1)
  );

  const [reloadKey, setReloadKey] = useState(0);

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    movement: null,
  });

  const [insight, setInsight] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState("");

  const tab =
    searchParams.get("tab") === "analysis"
      ? "analysis"
      : "movements";

  const userName = useMemo(
    () => readAuth(AUTH_KEYS.USER_NAME) || "",
    []
  );

  const isPremium = useMemo(
    () => canUsePremiumFinanceAI(),
    []
  );

  const summary = useMemo(
    () => data?.resumo || {},
    [data?.resumo]
  );

  const pending = data?.pendencias || {};

  const pagination = data?.movimentacoes || {
    items: [],
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  };

  const health = useMemo(
    () => getHealthInfo(summary),
    [summary]
  );

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      toast.error({
        title: "Sessão encerrada",
        message: "Faça login novamente para acessar o financeiro.",
        duration: 6500,
      });

      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate, toast]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 320);

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);

    next.set("tab", tab);
    next.set("period", period);

    if (type && type !== "todos") {
      next.set("type", type);
    } else {
      next.delete("type");
    }

    if (sort && sort !== "date-desc") {
      next.set("sort", sort);
    } else {
      next.delete("sort");
    }

    if (page > 1) {
      next.set("page", String(page));
    } else {
      next.delete("page");
    }

    setSearchParams(next, { replace: true });
  }, [
    page,
    period,
    setSearchParams,
    sort,
    tab,
    type,
  ]);

  const fetchCentral = useCallback(
    async ({ silent = false, signal } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const params = new URLSearchParams({
          id: auth.userId,
          periodo: period,
          tipo: type,
          sort,
          page: String(page),
          limit: "10",
        });

        if (debouncedSearch) {
          params.set("busca", debouncedSearch);
        }

        const response = await fetch(
          `${API_BASE}/psy/finance/central?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error({
            title: "Sessão encerrada",
            message: "Sua sessão expirou. Faça login novamente.",
            duration: 6500,
          });

          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || "Não foi possível carregar o financeiro.");
          setData(null);
          return;
        }

        const payload = await response.json();

        setData(payload || null);

        if (
          safeNumber(payload?.movimentacoes?.page) >
          safeNumber(payload?.movimentacoes?.totalPages)
        ) {
          setPage(
            Math.max(
              1,
              safeNumber(payload?.movimentacoes?.totalPages, 1)
            )
          );
        }
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar central financeira:", err);
        setData(null);
        setError("Falha de comunicação com a API.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      authOrRedirect,
      debouncedSearch,
      navigate,
      page,
      period,
      sort,
      toast,
      type,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchCentral({
      signal: controller.signal,
    });

    return () => controller.abort();
  }, [fetchCentral, reloadKey]);

  useEffect(() => {
    if (
      !location.state?.refresh &&
      !location.state?.refreshFinance &&
      !location.state?.refreshExpenses &&
      !location.state?.refreshRevenues
    ) {
      return;
    }

    setReloadKey((current) => current + 1);

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {},
    });
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const setTab = useCallback(
    (nextTab) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", nextTab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const reload = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);

  const changePeriod = (event) => {
    setPeriod(event.target.value);
    setPage(1);
  };

  const changeType = (nextType) => {
    setType(nextType);
    setPage(1);
  };

  const changeSort = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  const editMovement = (movement) => {
    if (!movement) return;

    if (movement.tipo === "despesa") {
      navigate("/finance/expense/edit", {
        state: {
          expense: movement.record,
        },
      });

      return;
    }

    navigate("/finance/revenue/edit", {
      state: {
        revenue: movement.record,
      },
    });
  };

  const askDelete = (movement) => {
    if (!movement?.sourceId) {
      toast.warning({
        title: "Movimentação inválida",
        message: "Não foi possível identificar o lançamento selecionado.",
      });

      return;
    }

    setConfirmDelete({
      open: true,
      movement,
    });
  };

  const closeDelete = () => {
    if (deleting) return;

    setConfirmDelete({
      open: false,
      movement: null,
    });
  };

  const doDelete = async () => {
    const movement = confirmDelete.movement;

    if (!movement?.sourceId) {
      closeDelete();
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setDeleting(true);

    try {
      const route =
        movement.tipo === "despesa"
          ? `/psy/payment/${encodeURIComponent(movement.sourceId)}`
          : `/psy/receita/${encodeURIComponent(movement.sourceId)}`;

      const params = new URLSearchParams({
        id: auth.userId,
      });

      const response = await fetch(
        `${API_BASE}${route}?${params.toString()}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        toast.error({
          title: "Sessão encerrada",
          message: "Faça login novamente para continuar.",
          duration: 6500,
        });

        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);

        toast.error({
          title: "Não foi possível excluir",
          message,
        });

        return;
      }

      toast.success({
        title:
          movement.tipo === "despesa"
            ? "Despesa excluída"
            : "Receita excluída",
        message: "A central financeira foi atualizada.",
      });

      setConfirmDelete({
        open: false,
        movement: null,
      });

      setReloadKey((current) => current + 1);
    } catch (err) {
      console.error("Erro ao excluir movimentação:", err);

      toast.error({
        title: "Falha de comunicação",
        message: "Não foi possível concluir a exclusão agora.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const generateInsight = async () => {
    if (!isPremium) {
      navigate("/plan-management");
      return;
    }

    const auth = authOrRedirect();

    if (!auth || !data) return;

    setInsightLoading(true);
    setInsightError("");

    try {
      const evolution = (data?.evolucaoMensal || [])
        .map(
          (item) =>
            `${item.label}: receita ${moneyBR(
              item.receita
            )}, despesa ${moneyBR(
              item.despesa
            )}, saldo ${moneyBR(item.saldo)}`
        )
        .join("\n");

      const details = [
        `Período: ${formatPeriodLabel(data?.periodo)}`,
        `Receitas: ${moneyBR(summary.receita)}`,
        `Despesas: ${moneyBR(summary.despesa)}`,
        `Saldo: ${moneyBR(summary.saldo)}`,
        `Margem: ${percentBR(summary.margem)}`,
        `Risco financeiro: ${percentBR(summary.riscoFinanceiro)}`,
        `Parcelas em aberto: ${safeNumber(
          pending.parcelasAbertas
        )} / ${moneyBR(pending.valorAberto)}`,
        `Parcelas vencidas: ${safeNumber(
          pending.parcelasVencidas
        )} / ${moneyBR(pending.valorVencido)}`,
        evolution ? `Evolução:\n${evolution}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const response = await fetch(
        `${API_BASE}/psy/insight/payment?id=${encodeURIComponent(
          auth.userId
        )}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: auth.userId,
            paymentDetails: details,
          }),
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401) {
        toast.error({
          title: "Sessão encerrada",
          message: "Faça login novamente para continuar.",
          duration: 6500,
        });

        navigate("/login", { replace: true });
        return;
      }

      if (response.status === 403) {
        const message = await readApiError(response);
        setInsight("");
        setInsightError(
          message || "Recurso disponível apenas no plano Premium."
        );
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);
        setInsight("");
        setInsightError(
          message || "Não foi possível gerar a análise financeira."
        );
        return;
      }

      const payload = await response.json();
      setInsight(String(payload?.insight || "").trim());
    } catch (err) {
      console.error("Erro ao gerar insight financeiro:", err);
      setInsight("");
      setInsightError(
        "Falha de comunicação ao gerar a análise financeira."
      );
    } finally {
      setInsightLoading(false);
    }
  };

  const periodLabel = formatPeriodLabel(data?.periodo);

  const updatedLabel = data?.updatedAt
    ? formatTimeBR(data.updatedAt)
    : "";

  const balance = safeNumber(summary.saldo);

  const totalPages = Math.max(
    1,
    safeNumber(pagination.totalPages, 1)
  );

  return (
    <div className="finc-page">
      <div className="finc-bg" aria-hidden="true">
        <span className="finc-bg__orb finc-bg__orb--one" />
        <span className="finc-bg__orb finc-bg__orb--two" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <header className="finc-topbar">
        <button
          className="finc-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="finc-brand">
          <img
            src={Logo}
            alt="PsyAssist"
            className="finc-brand__logo"
          />

          <div className="finc-brand__text">
            <strong>Financeiro</strong>

            <span>
              Central de gestão financeira
              {" • "}
              {userName || "Usuário"}
            </span>
          </div>
        </div>

        <div className="finc-top-actions">
          <button
            className="finc-btn finc-btn--ghost finc-btn--desktop"
            type="button"
            onClick={() => navigate("/reports/saude")}
          >
            <FileHeart size={16} aria-hidden="true" />
            Receita Saúde
          </button>

          <button
            className="finc-btn finc-btn--ghost finc-btn--desktop"
            type="button"
            onClick={() =>
              fetchCentral({
                silent: true,
              })
            }
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "finc-spin" : ""}
              aria-hidden="true"
            />
            Atualizar
          </button>

          <button
            className="finc-btn finc-btn--primary"
            type="button"
            onClick={() => navigate("/finance/revenue/new")}
          >
            <Plus size={17} aria-hidden="true" />
            Receita
          </button>
        </div>
      </header>

      <main className="finc-main">
        {loading && !data ? (
          <FinanceSkeleton />
        ) : (
          <>
            <section className="finc-hero">
              <div className="finc-hero__content">
                <span className="finc-kicker">
                  <WalletCards size={15} aria-hidden="true" />
                  Central financeira
                </span>

                <h1>
                  Seu financeiro,
                  sem telas espalhadas.
                </h1>

                <p>
                  Acompanhe entradas, saídas, saldo, movimentações,
                  pendências e análise financeira em um só lugar.
                </p>

                <div className="finc-hero__actions">
                  <button
                    className="finc-btn finc-btn--primary"
                    type="button"
                    onClick={() => navigate("/finance/revenue/new")}
                  >
                    <TrendingUp size={17} />
                    Lançar receita
                  </button>

                  <button
                    className="finc-btn finc-btn--hero-ghost"
                    type="button"
                    onClick={() => navigate("/finance/expense/new")}
                  >
                    <TrendingDown size={17} />
                    Lançar despesa
                  </button>

                  <button
                    className="finc-btn finc-btn--hero-ghost"
                    type="button"
                    onClick={() => navigate("/reports/saude")}
                  >
                    <FileHeart size={17} />
                    Receita Saúde
                  </button>
                </div>
              </div>

              <aside
                className={`finc-health finc-health--${health.tone}`}
              >
                <div className="finc-health__top">
                  <div className="finc-health__icon">
                    <Landmark size={25} />
                  </div>

                  <span>Saúde financeira</span>
                </div>

                <strong>{health.label}</strong>
                <h2>{health.title}</h2>
                <p>{health.description}</p>

                <div className="finc-health__period">
                  <span>Período</span>
                  <b>{periodLabel}</b>
                </div>
              </aside>
            </section>

            {error ? (
              <section className="finc-error-card">
                <div>
                  <CircleAlert size={22} />
                </div>

                <div>
                  <strong>
                    Não foi possível carregar o financeiro
                  </strong>

                  <span>{error}</span>
                </div>

                <button
                  className="finc-btn finc-btn--primary"
                  type="button"
                  onClick={reload}
                >
                  Tentar novamente
                </button>
              </section>
            ) : null}

            <section className="finc-metrics">
              <SummaryMetric
                icon={ArrowUpRight}
                eyebrow="Entradas"
                value={moneyBR(summary.receita)}
                detail={`${safeNumber(
                  summary.receitasCount
                )} receita(s) no período`}
                tone="green"
              />

              <SummaryMetric
                icon={ArrowDownRight}
                eyebrow="Saídas"
                value={moneyBR(summary.despesa)}
                detail={`${safeNumber(
                  summary.despesasCount
                )} despesa(s) no período`}
                tone="red"
              />

              <SummaryMetric
                icon={WalletCards}
                eyebrow="Saldo"
                value={moneyBR(summary.saldo)}
                detail={
                  balance >= 0
                    ? "Resultado positivo"
                    : "Resultado negativo"
                }
                tone={balance >= 0 ? "blue" : "red"}
              />

              <SummaryMetric
                icon={BarChart3}
                eyebrow="Margem"
                value={percentBR(summary.margem)}
                detail={`Risco ${percentBR(
                  summary.riscoFinanceiro
                )}`}
                tone="violet"
              />
            </section>

            <section className="finc-console">
              <header className="finc-console__head">
                <div>
                  <span>Gestão financeira</span>
                  <h2>O que você precisa ver agora?</h2>
                  <p>
                    Alterne entre a operação diária e a leitura financeira
                    sem sair desta tela.
                  </p>
                </div>

                <div className="finc-period-control">
                  <Filter size={15} />

                  <select
                    value={period}
                    onChange={changePeriod}
                    aria-label="Período financeiro"
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </header>

              <div
                className="finc-tabs"
                role="tablist"
                aria-label="Seções do financeiro"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "movements"}
                  className={tab === "movements" ? "is-active" : ""}
                  onClick={() => setTab("movements")}
                >
                  <ReceiptText size={16} />
                  Movimentações
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "analysis"}
                  className={tab === "analysis" ? "is-active" : ""}
                  onClick={() => setTab("analysis")}
                >
                  <BarChart3 size={16} />
                  Análise financeira
                </button>
              </div>

              {tab === "movements" ? (
                <section className="finc-movements">
                  <div className="finc-toolbar">
                    <label className="finc-search">
                      <Search size={16} aria-hidden="true" />

                      <input
                        value={search}
                        onChange={(event) =>
                          setSearch(event.target.value)
                        }
                        placeholder="Buscar por descrição, categoria, valor ou data..."
                      />

                      {search ? (
                        <button
                          type="button"
                          onClick={() => setSearch("")}
                          aria-label="Limpar busca"
                        >
                          <X size={15} />
                        </button>
                      ) : null}
                    </label>

                    <div className="finc-type-filter">
                      {MOVEMENT_TYPES.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          className={
                            type === option.value ? "is-active" : ""
                          }
                          onClick={() => changeType(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <select
                      className="finc-sort"
                      value={sort}
                      onChange={changeSort}
                      aria-label="Ordenar movimentações"
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="finc-movement-summary">
                    <span>
                      {pagination.total} movimentação(ões)
                    </span>

                    <small>
                      {updatedLabel
                        ? `Atualizado às ${updatedLabel}`
                        : periodLabel}
                    </small>
                  </div>

                  {refreshing ? (
                    <div className="finc-refreshing">
                      <LoaderCircle size={16} className="finc-spin" />
                      Atualizando movimentações...
                    </div>
                  ) : null}

                  {!pagination.items?.length ? (
                    <div className="finc-empty">
                      <ReceiptText size={28} />

                      <strong>
                        Nenhuma movimentação encontrada
                      </strong>

                      <span>
                        Ajuste os filtros ou registre uma nova receita ou
                        despesa.
                      </span>

                      <div>
                        <button
                          className="finc-btn finc-btn--primary"
                          type="button"
                          onClick={() =>
                            navigate("/finance/revenue/new")
                          }
                        >
                          <Plus size={16} />
                          Receita
                        </button>

                        <button
                          className="finc-btn finc-btn--ghost"
                          type="button"
                          onClick={() =>
                            navigate("/finance/expense/new")
                          }
                        >
                          <Plus size={16} />
                          Despesa
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="finc-movement-list">
                      {pagination.items.map((movement) => {
                        const Icon =
                          movement.tipo === "despesa"
                            ? TrendingDown
                            : TrendingUp;

                        const isExpense =
                          movement.tipo === "despesa";

                        return (
                          <article
                            className={`finc-movement finc-movement--${movement.tipo}`}
                            key={movement.id}
                          >
                            <div className="finc-movement__icon">
                              <Icon size={19} />
                            </div>

                            <div className="finc-movement__content">
                              <div className="finc-movement__title">
                                <strong>
                                  {movement.descricao ||
                                    movement.categoria ||
                                    (isExpense
                                      ? "Despesa"
                                      : "Receita")}
                                </strong>

                                <span
                                  className={`finc-movement__type finc-movement__type--${movement.tipo}`}
                                >
                                  {isExpense ? "Despesa" : "Receita"}
                                </span>
                              </div>

                              <div className="finc-movement__meta">
                                <span>{movement.categoria}</span>
                                <i />
                                <span>{formatDateBR(movement.data)}</span>

                                {movement?.parcelasPeriodo > 0 ? (
                                  <>
                                    <i />
                                    <span>
                                      {movement.parcelasPeriodo} parcela(s)
                                      no período
                                    </span>
                                  </>
                                ) : null}

                                {movement?.proximoVencimento ? (
                                  <>
                                    <i />
                                    <span>
                                      Próximo:{" "}
                                      {formatDateBR(
                                        movement.proximoVencimento
                                          .vencimento
                                      )}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            </div>

                            <div className="finc-movement__value">
                              <strong>
                                {isExpense ? "− " : "+ "}
                                {moneyBR(movement.valor)}
                              </strong>

                              <span>
                                {isExpense
                                  ? "saída no período"
                                  : "entrada"}
                              </span>
                            </div>

                            <div className="finc-movement__actions">
                              <button
                                type="button"
                                onClick={() =>
                                  editMovement(movement)
                                }
                                aria-label={`Editar ${
                                  isExpense ? "despesa" : "receita"
                                }`}
                                title="Editar"
                              >
                                <Pencil size={16} />
                              </button>

                              <button
                                type="button"
                                className="is-danger"
                                onClick={() => askDelete(movement)}
                                aria-label={`Excluir ${
                                  isExpense ? "despesa" : "receita"
                                }`}
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}

                  {pagination.total > 0 ? (
                    <footer className="finc-pagination">
                      <span>
                        Página {pagination.page} de {totalPages}
                      </span>

                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            setPage((current) =>
                              Math.max(1, current - 1)
                            )
                          }
                          disabled={page <= 1}
                          aria-label="Página anterior"
                        >
                          <ChevronLeft size={17} />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setPage((current) =>
                              Math.min(totalPages, current + 1)
                            )
                          }
                          disabled={page >= totalPages}
                          aria-label="Próxima página"
                        >
                          <ChevronRight size={17} />
                        </button>
                      </div>
                    </footer>
                  ) : null}
                </section>
              ) : (
                <section className="finc-analysis">
                  <div className="finc-analysis__grid">
                    <article className="finc-panel finc-panel--chart">
                      <div className="finc-panel__head">
                        <div>
                          <span>Evolução</span>
                          <h3>Entradas x saídas</h3>
                          <p>Comparativo dos últimos seis meses.</p>
                        </div>
                      </div>

                      <EvolutionChart
                        rows={data?.evolucaoMensal}
                      />
                    </article>

                    <article className="finc-panel finc-panel--risk">
                      <div className="finc-panel__head">
                        <div>
                          <span>Risco</span>
                          <h3>Comprometimento da receita</h3>
                          <p>
                            Percentual das receitas consumido pelas despesas.
                          </p>
                        </div>

                        <strong
                          className={`finc-risk-tag finc-risk-tag--${health.tone}`}
                        >
                          {health.label}
                        </strong>
                      </div>

                      <div className="finc-risk">
                        <strong>
                          {percentBR(summary.riscoFinanceiro)}
                        </strong>

                        <div className="finc-risk__track">
                          <span
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(
                                  0,
                                  safeNumber(summary.riscoFinanceiro)
                                )
                              )}%`,
                            }}
                          />
                        </div>

                        <p>{health.description}</p>
                      </div>
                    </article>

                    <article className="finc-panel">
                      <div className="finc-panel__head">
                        <div>
                          <span>Custos</span>
                          <h3>Onde você está gastando</h3>
                          <p>
                            Principais categorias de despesa no período.
                          </p>
                        </div>
                      </div>

                      <CategoryDistribution
                        rows={data?.categorias?.despesas}
                      />
                    </article>

                    <article className="finc-panel">
                      <div className="finc-panel__head">
                        <div>
                          <span>Compromissos</span>
                          <h3>Parcelas e vencimentos</h3>
                          <p>
                            Visão rápida do que ainda exige atenção.
                          </p>
                        </div>
                      </div>

                      <div className="finc-obligations">
                        <article>
                          <CalendarClock size={18} />

                          <div>
                            <span>Em aberto</span>
                            <strong>
                              {moneyBR(pending.valorAberto)}
                            </strong>
                            <small>
                              {safeNumber(
                                pending.parcelasAbertas
                              )}{" "}
                              parcela(s)
                            </small>
                          </div>
                        </article>

                        <article
                          className={
                            safeNumber(pending.valorVencido) > 0
                              ? "is-warning"
                              : ""
                          }
                        >
                          <CircleAlert size={18} />

                          <div>
                            <span>Vencidas</span>
                            <strong>
                              {moneyBR(pending.valorVencido)}
                            </strong>
                            <small>
                              {safeNumber(
                                pending.parcelasVencidas
                              )}{" "}
                              parcela(s)
                            </small>
                          </div>
                        </article>

                        <article>
                          <CheckCircle2 size={18} />

                          <div>
                            <span>Próximo vencimento</span>
                            <strong>
                              {pending?.proximoVencimento?.vencimento
                                ? formatDateBR(
                                    pending.proximoVencimento
                                      .vencimento
                                  )
                                : "Sem pendências"}
                            </strong>
                            <small>
                              {pending?.proximoVencimento?.valor
                                ? moneyBR(
                                    pending.proximoVencimento.valor
                                  )
                                : "Tudo em dia"}
                            </small>
                          </div>
                        </article>
                      </div>
                    </article>
                  </div>

                  <article className="finc-ai">
                    <div className="finc-ai__head">
                      <div className="finc-ai__brand">
                        <span>
                          <BrainCircuit size={18} />
                        </span>

                        <div>
                          <small>PSYA Premium</small>
                          <strong>Análise financeira</strong>
                        </div>
                      </div>

                      {isPremium ? (
                        <button
                          className="finc-ai__button"
                          type="button"
                          onClick={generateInsight}
                          disabled={insightLoading}
                        >
                          {insightLoading ? (
                            <>
                              <LoaderCircle
                                size={16}
                                className="finc-spin"
                              />
                              Analisando...
                            </>
                          ) : (
                            <>
                              <Sparkles size={16} />
                              Gerar insight
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          className="finc-ai__button"
                          type="button"
                          onClick={() =>
                            navigate("/plan-management")
                          }
                        >
                          Ver Premium
                          <ArrowRight size={16} />
                        </button>
                      )}
                    </div>

                    {insightError ? (
                      <div className="finc-ai__error">
                        <CircleAlert size={17} />
                        {insightError}
                      </div>
                    ) : null}

                    {!isPremium ? (
                      <div className="finc-ai__empty">
                        <ShieldCheck size={24} />

                        <div>
                          <strong>
                            Recurso disponível no Premium
                          </strong>

                          <span>
                            A PSYA pode analisar saldo, custos, evolução,
                            risco e pendências para apoiar sua gestão.
                          </span>
                        </div>
                      </div>
                    ) : insightLoading ? (
                      <div className="finc-ai__empty">
                        <LoaderCircle
                          size={25}
                          className="finc-spin"
                        />

                        <div>
                          <strong>
                            Analisando seu financeiro
                          </strong>

                          <span>
                            A PSYA está organizando os indicadores do período.
                          </span>
                        </div>
                      </div>
                    ) : insight ? (
                      <div className="finc-ai__result">
                        <Sparkles size={20} />
                        <p>{insight}</p>
                      </div>
                    ) : (
                      <div className="finc-ai__empty">
                        <BrainCircuit size={25} />

                        <div>
                          <strong>
                            Leitura pronta para gerar
                          </strong>

                          <span>
                            Gere a análise quando quiser. O insight não é
                            disparado automaticamente.
                          </span>
                        </div>
                      </div>
                    )}
                  </article>
                </section>
              )}
            </section>

            <section className="finc-footer-actions">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft size={16} />
                Voltar ao início
              </button>

              <span>
                {updatedLabel
                  ? `Dados atualizados às ${updatedLabel}`
                  : "Central financeira"}
              </span>
            </section>
          </>
        )}
      </main>

      <button
        className="finc-fab"
        type="button"
        onClick={() => navigate("/finance/revenue/new")}
        aria-label="Lançar receita"
      >
        <Plus size={23} />
      </button>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        movement={confirmDelete.movement}
        loading={deleting}
        onClose={closeDelete}
        onConfirm={doDelete}
      />
    </div>
  );
}
